import React from "react";
import {autorun, reaction} from "mobx";
import {observer} from "mobx-react";

import "./layer-selector.scss";

import {SidebarControlsConfiguration} from "../../../config/sidebar-configuration";
import {Legend as CoreLegend} from "../legend/legend";
import {Legend as CustomLegend} from "../../../components/app/sidebar/legend/legend";
import {Button, Divider, Form} from "semantic-ui-react";

import EsriLegendViewModel from "@arcgis/core/widgets/Legend/LegendViewModel";

import {LayerSelectorMatrix} from "./layer-selector-matrix/layer-selector-matrix";

import {
    LayerSelectorConfiguration, LayerSelectorGroupConfiguration
} from "../../config/layer-selector-configuration";
import {IAppStore, withStore} from "../../stores/abstract-app-store";
import {LayerList} from "../layer-list/layer-list";
import {Icon} from "../icon/icon";
import {TopicSwitcherButton} from "../topic-switcher/topic-switcher-button";
import {TopicToggle} from "../topic-toggle/topic-toggle";

export interface LayerRules {
    filter?: string;
    visible?: boolean;
    renderField?: string;
}
export type ServiceRules = Map<string, Map<number, LayerRules>>;

export interface LayerSelectorProps {
    config: LayerSelectorConfiguration[];
    controlsConfig: SidebarControlsConfiguration;
    keyAppend?: string | number | undefined;
    onLoad?: (legendViewModel: EsriLegendViewModel) => any | undefined;
    small?: boolean;
    standalone?: boolean;
    store?: IAppStore; // this is injected by withStore
}


const LayerSelectorLegend = (controlsConfig: SidebarControlsConfiguration, small: boolean | undefined) => {
    if (controlsConfig.legend.position === "compare") {
        if (controlsConfig.legend.useEsri && controlsConfig.legend.esriConfig) {
            return <CoreLegend
                config={controlsConfig.legend.esriConfig}
                controlsConfig={controlsConfig}
                small={!!small} />;
        } else {
            return <CustomLegend
                config={controlsConfig.legend} />;
        }
    }
    return null;
};

@observer
class LayerSelectorBase extends React.Component<LayerSelectorProps, any> {
    watchHandles: any[] = [];
    autorunDisposers: any[] = [];
    autorunLayerSelectorDisposers: any[] = [];
    widgetKey = "layer-selector";

    constructor(props: LayerSelectorProps) {
        super(props);
        this.state = {};

        this.toggleComparison = this.toggleComparison.bind(this);
        this.handleChange = this.handleChange.bind(this);

        if (typeof this.props.keyAppend === "string" || typeof this.props.keyAppend === "number") {
            this.widgetKey += `-${this.props.keyAppend.toString()}`;
        }
        if (typeof this.props.small === "boolean" && this.props.small) {
            this.widgetKey += "-small";
        }
    }

    componentDidMount(): void {
        const {store} = this.props;
        if (!store) {
            return;
        }
        store.uiStore.addWidget(this.widgetKey, this);

        // Update matrix and applied rules on topic changes
        this.autorunDisposers.push(
            autorun(() => {
                if (!store.mapStore.view || !store.appStore.subtopic) {
                    return;
                }

                const {subtopic} = store.appStore;
                if (subtopic.id === store.LayerSelectorStore.subtopicId) {
                    // do nothing if subtopic did not change
                    return;
                }

                // matrix config is derived from subtopic
                // updates matrix rules on subtopic change
                store.LayerSelectorStore.setSubtopic(subtopic);

                // load dropdown options for new subtopic
                // do this after setting subtopic to avoid unnecessary loading
                store.LayerSelectorStore.loadDropdownOptions();

                if (store.LayerSelectorStore.isInitialized) {
                    // do nothing if store is already initialized for this subtopic
                    return;
                }

                // reapply matrix rules
                store.LayerSelectorStore.applyMatrixRules();
            })
        );
    }
    // noinspection DuplicatedCode
    componentWillUnmount(): void {
        const {store} = this.props;
        this.watchHandles.forEach((handle: any) => {
            handle.remove();
        });
        this.watchHandles.length = 0;
        this.autorunDisposers.forEach((disposer: any) => {
            disposer();
        });
        this.autorunDisposers.length = 0;
        store?.uiStore.removeWidget(this.widgetKey);
    }

    render(): React.ReactElement | null {
        const {
            controlsConfig,
            small,
            store
        } = this.props;

        if (!store) {
            return null;
        }

        const {subtopic} = store?.appStore;
        if (!subtopic) {
            return null;
        }
        const comparisonEnabled = store.LayerSelectorStore.comparisonEnabled;
        const matrixConfig = store.LayerSelectorStore.matrixConfig;

        const legend = LayerSelectorLegend(controlsConfig, small);
        const selects: React.ReactElement[] = [];

        if (matrixConfig && matrixConfig.layerMatrix) {
            selects.push(
                <LayerSelectorMatrix
                    key={"leading"}
                    comparisonEnabled={comparisonEnabled}
                    config={matrixConfig.layerMatrix}
                    onSelect={(groupConfig: LayerSelectorGroupConfiguration, groupId: string, value: string) => {
                        this.handleChange(groupConfig, groupId, value);
                    }}
                />
            );
            if (comparisonEnabled) {
                selects.push(
                    <Divider horizontal>vergleichen mit</Divider>
                );
                selects.push(
                    <LayerSelectorMatrix
                        key={"trailing"}
                        comparisonEnabled={comparisonEnabled}
                        config={matrixConfig.layerMatrix}
                        onSelect={(groupConfig: LayerSelectorGroupConfiguration, groupId: string, value: string) => {
                            this.handleChange(groupConfig, groupId, value, true);
                        }}
                        trailing={true}
                    />
                );
            }
        }

        let cssClass = `geo-layer-selector-panel geo-orientation-${store.uiStore.uiOrientation}`;
        if (comparisonEnabled) {
            cssClass += " geo-compare-enabled";
        }
        return (
            <div className={cssClass}>
                <div className={"geo-topic_switcher-compare_button"}>
                    <TopicSwitcherButton topics={store.mapViewerConfig.subtopics} />
                    {!matrixConfig?.hideCompareButton &&
                    <Button
                        key={"image"}
                        primary={comparisonEnabled}
                        color={"orange"}
                        compact
                        onClick={(e) => {
                            e.preventDefault();
                            e.currentTarget.blur();
                            this.toggleComparison();
                        }}
                        size={"mini"}>
                        <Icon name={"documents_empty"}/>&nbsp;
                        {comparisonEnabled ? "Vergleichen beenden" : "Vergleichen"}
                    </Button>
                    }
                </div>
                <div className="geo-layer-selector-wrapper">
                    <Form size={"small"}>
                        <TopicToggle config={matrixConfig?.topicToggle} />
                        {selects}
                    </Form>
                </div>
                <div className={`geo-layer-selector-layer-list${selects.length > 0 ? " with-selectors" : ""}`}>
                    <LayerList
                        controlsConfig={controlsConfig}
                        config={controlsConfig.layerList}
                        hideList={matrixConfig?.hideLayerList}
                        small={!!small} />
                </div>
                {legend}
            </div>
        );
    }

    private handleChange(groupConfig: LayerSelectorGroupConfiguration,
                         groupId: string,
                         value: string,
                         trailing: boolean = false): void {
        const { store} = this.props;
        if (!store || !store.appStore.subtopic) {
            return;
        }

        store.LayerSelectorStore.setSelectedOption(groupId, value, trailing);
        store.LayerSelectorStore.applyMatrixRules(trailing);
    }

    private toggleComparison(): void {
        const {store} = this.props;

        if (!store?.LayerSelectorStore.comparisonEnabled) {
            this.enableComparison();
        } else {
            this.disableComparison();
        }
    }
    private enableComparison(): void {
        const {store} = this.props;
        if (!store || !store.mapStore.view || !store?.appStore.subtopic) {
            return;
        }

        store.LayerSelectorStore.enableComparison()
            .then(() => {
                this.addComparisonHandles();
                store.PopupStore.setVisible(false);
                store.PopupStore.setDisabled(true);
            });
    }
    private disableComparison(): void {
        const {store} = this.props;

        store?.LayerSelectorStore.disableComparison().then(() => {
            store?.PopupStore.setDisabled(false);
            this.removeComparisonHandles();
        });
    }

    private addComparisonHandles(): void {
        const {store} = this.props;
        this.autorunLayerSelectorDisposers.push(
            reaction(
                () => store?.uiStore.uiOrientation,
                () => {
                    const direction = store?.uiStore.uiOrientation === "portrait" ? "vertical" : "horizontal";
                    store?.LayerSelectorStore.updateOrientation(direction);
                }
            )
        );
    }
    private removeComparisonHandles(): void {
        this.autorunLayerSelectorDisposers.forEach((disposer: any) => {
            disposer();
        });
        this.autorunLayerSelectorDisposers.length = 0;
    }
}

export const LayerSelector = withStore(LayerSelectorBase);
